import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Space, Button, Typography, Row, Col, Card } from 'antd';
import { useNavigate } from "react-router-dom";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { TbChartCohort } from "react-icons/tb";
import { HiMiniUsers } from "react-icons/hi2";
import ListItemsDataTable from "../../components/crud/ListItemsDataTable.component";
import DynamicFormSP from "../../components/crud/DynamicFormSP.component";
import { fieldEntityActionConfig } from "../../components/crud/utils/fieldEntityActionConfig";
import PageWrapper from "../../components/common/PageWrapper";
import SectionHeading from "../../components/common/SectionHeading";
import ApiErrorAlert from "../../components/common/ApiErrorAlert";
import { normalizeApiErrorText, showApiError } from "../../utils/formatApiError";

export default function EntityManagementScreen({ 
  entityName, 
  beautifulName,
  fieldsToHide = ["Id", "CreatedAt", "UpdatedAt"],
  showEntityEditById = null,
  showCreateForm = false,
  showListByDefault = true,
  icon,
  initialListFilter = null,
  extraHeaderActions = null,
  /** Optional: render extra content (e.g. related files) below the form when editing an existing record. */
  renderDetailExtra = null,
  /** Optional: navigate to a dedicated route instead of showing the inline Create form. */
  onCreateClick = null,
  /** Optional: navigate to a dedicated route instead of showing the inline Edit/View form. */
  onEditClick = null,
  /** Optional: fires once after a successful create (not on details hydrate / update).
   *  Use to redirect a create-only route to the new record's permanent URL. */
  onEntityReady = null,
  /** Optional: navigate back to the list route instead of flipping local list/detail state
   *  (required when onCreateClick/onEditClick put Create/Edit on their own routes). */
  onBackClick = null,
  /** Whether this screen offers creation at all — both the list header button and DynamicFormSP's
   *  inline "switch to Create" action. Screens that already have a dedicated Create route (e.g.
   *  Patient) should pass false so there's only ever one way to create a new record; screens whose
   *  records are never authored by hand (e.g. VaultPatient) pass false to remove creation. */
  enableCreate = true,
  /** Optional kicker/title for the entity form card (e.g. patient name). */
  sectionKicker = null,
  sectionTitle = null,
  /** Optional right-hand help panel content (PageWrapper helpContent). */
  helpContent = null,
  /** Optional tabs rendered inside PageWrapper (same slot as FeedbackManagement-style layouts). */
  pageTabs = null,
}) {
  const { t } = useTranslation();
  const { Text } = Typography;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get icon based on entity name if not provided
  const getEntityIcon = () => {
    if (icon) return icon;
    if (entityName === 'Cohort') return <TbChartCohort size={24} />;
    if (entityName === 'Patient') return <HiMiniUsers size={24} />;
    return null;
  };

  const getInitialViewState = () => {
    if (showEntityEditById) {
      return { isListView: false, selectedEntityId: showEntityEditById };
    }
    if (showCreateForm) {
      return { isListView: false, selectedEntityId: null };
    }
    return { isListView: showListByDefault, selectedEntityId: null };
  };

  const initialState = getInitialViewState();
  const [isListView, setIsListView] = useState(initialState.isListView);
  const [selectedEntityId, setSelectedEntityId] = useState(initialState.selectedEntityId);

  const entityConfig = fieldEntityActionConfig[`${entityName}Ids`];
  const singularEntityLabel = t(entityConfig?.singularEntityLabelKey || entityConfig?.singularEntity || entityName);
  const pluralEntityLabel = t(entityConfig?.pluralEntityLabelKey || entityConfig?.pluralEntity || `${entityName.toLowerCase()}s`);
  const defaultPageSize = 10;
  const [listFilter, setListFilter] = useState(
    initialListFilter ?? {
      query: "",
      page: 1,
      limit: defaultPageSize,
      filter: entityConfig.primarySearchProp
    }
  );
  const pageSize = listFilter.limit ?? defaultPageSize;

  const entityListState = useSelector((state) => state[`${entityConfig.singularEntity}List`]);
  const { loading, error, [entityConfig.pluralEntity]: entities, currentPage, totalPages, totalCount } = entityListState;
  const listErrorText = normalizeApiErrorText(error);

  // Delete lives in its own slice. Watching only the list error is why a failed Patient
  // delete was silent on /patients and then popped a stale notification on another screen.
  const entityDeleteState = useSelector((state) => state[`${entityConfig.singularEntity}Delete`] || {});
  const deleteErrorText = normalizeApiErrorText(entityDeleteState.error);

  const entityDetailsState = useSelector((state) => state[`${entityConfig.singularEntity}Details`]);
  const detailsRecord = entityDetailsState?.[entityConfig.singularEntity];
  const detailsLoading = entityDetailsState?.loading === true;
  const detailsError = entityDetailsState?.error;

  const dtoConfig = useSelector((state) => state[`${entityConfig.singularEntity}Dto`]);
  const { [`${entityConfig.singularEntity}Dto`]: entityDtoConfig } = dtoConfig;

  useEffect(() => {
    dispatch(entityConfig.listEntities(listFilter));
    dispatch(entityConfig.loadDto());
  }, [dispatch, entityConfig, listFilter]);

  useEffect(() => {
    if (listErrorText) showApiError(listErrorText, t);
  }, [listErrorText, t]);

  useEffect(() => {
    if (!deleteErrorText) return;
    showApiError(deleteErrorText, t);
    dispatch({ type: `${entityConfig.constantPrefix}_DELETE_RESET` });
  }, [deleteErrorText, t, dispatch, entityConfig.constantPrefix]);

  useEffect(() => {
    if (!entityDeleteState.success) return;
    dispatch(entityConfig.listEntities(listFilter));
    dispatch({ type: `${entityConfig.constantPrefix}_DELETE_RESET` });
  }, [entityDeleteState.success, dispatch, entityConfig, listFilter]);

  const handleCreate = () => {
    if (onCreateClick) {
      onCreateClick();
      return;
    }
    setIsListView(false);
    setSelectedEntityId(null);
  };

  const handleEdit = (id) => {
    if (onEditClick) {
      onEditClick(id);
      return;
    }
    setIsListView(false);
    setSelectedEntityId(id);
  };

  // Entities whose config omits deleteEntity (e.g. VaultPatient, which protocol §5.3 forbids
  // deleting) get no delete affordance at all — ListItemsDataTable renders the Delete column
  // only when handleDeleteRow is passed.
  const handleDelete = entityConfig.deleteEntity
    ? (id) => {
      dispatch(entityConfig.deleteEntity(id));
    }
    : null;

  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
      return;
    }
    setIsListView(true);
    setSelectedEntityId(null);
  };

  const renderMobileCard = (item) => (
    <Card
      key={item.id}
      style={{ marginBottom: 16 }}
      actions={[
        <Button icon={<EditOutlined />} onClick={() => handleEdit(item.id)}>Edit</Button>,
        ...(handleDelete
          ? [<Button icon={<DeleteOutlined />} onClick={() => handleDelete(item.id)} danger>Delete</Button>]
          : []),
      ]}
    >
      <Card.Meta
        title={item[entityConfig.primarySearchProp]}
        description={
          <Space direction="vertical">
            {entityConfig.summaryProps.map(prop => (
              <span key={prop}>{`${prop}: ${item[prop]}`}</span>
            ))}
          </Space>
        }
      />
    </Card>
  );

  const refreshList = () => dispatch(entityConfig.listEntities(listFilter));
  // List view always gets a Create action; screens with a custom one (e.g. Cohort's
  // "Create Upload Session") pass extraHeaderActions instead of the generic button.
  // Detail view relies on PageWrapper's own back-to-list link (isDetailsPage prop below)
  // instead of duplicating it here.
  const headerActions = isListView ? (
    <Space>
      {enableCreate && !extraHeaderActions && (
        <Button
          type="primary"
          onClick={handleCreate}
          icon={<PlusOutlined />}
          size="small"
        >
          {t("Create")} {singularEntityLabel}
        </Button>
      )}
      {typeof extraHeaderActions === 'function' ? extraHeaderActions(refreshList) : extraHeaderActions}
    </Space>
  ) : null;

  const managementLabel = `${beautifulName || singularEntityLabel} ${t("Management")}`;
  const backLabel = `${t("Back to")} ${managementLabel}`;

  return (
    <PageWrapper
      title={managementLabel}
      subtitle={
        isListView && totalCount !== undefined ? (
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {t("Total")}: {totalCount} {t("item(s)")}
          </Text>
        ) : null
      }
      icon={getEntityIcon()}
      headerActions={headerActions}
      showCard={true}
      isDetailsPage={!isListView}
      listPagePath={isListView ? null : `/${entityName.toLowerCase()}s`}
      entityName={entityName}
      backButtonLabel={!isListView ? backLabel : undefined}
      onGoToList={isListView ? null : handleBack}
      helpContent={!isListView ? helpContent : null}
      tabs={isListView ? pageTabs : null}
    >
      {(listErrorText || deleteErrorText) && <ApiErrorAlert error={deleteErrorText || error} />}
      {isListView ? (
        entityDtoConfig && (
          <ListItemsDataTable
            loading={loading}
            error={listErrorText}
            dtoConfig={entityDtoConfig}
            data={entities}
            filter={listFilter}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            limit={pageSize}
            onFilterChange={setListFilter}
            handleDeleteRow={handleDelete}
            handleEditForm={handleEdit}
            getEntityDetails={entityConfig.getEntityDetails}
            detailsRecord={detailsRecord}
            detailsLoading={detailsLoading}
            detailsError={detailsError}
            enableActionButtons={true}
            renderMobileCard={renderMobileCard}
            onRetry={refreshList}
          />
        )
      ) : (
        <div style={{ width: "100%" }}>
          {(sectionKicker || sectionTitle) && (
            <SectionHeading
              kicker={sectionKicker}
              title={typeof sectionTitle === 'function' ? sectionTitle(detailsRecord) : sectionTitle}
              style={{ marginBottom: 24 }}
            />
          )}
          <div
            className="entity-management-form-container"
            style={helpContent ? { width: '100%' } : undefined}
          >
            <DynamicFormSP
              currentEntityId={selectedEntityId}
              selectedItemIds={selectedEntityId ? [selectedEntityId] : []}
              defaultEditMode={selectedEntityId ? "Edit" : "Create"}
              primarySearchProp={entityConfig.primarySearchProp}
              secondarySearchProp={entityConfig.secondarySearchProp}
              singularEntity={entityConfig.singularEntity}
              pluralEntity={entityConfig.pluralEntity}
              constantPrefix={entityConfig.constantPrefix}
              loadDto={entityConfig.loadDto}
              createEntity={entityConfig.createEntity}
              updateEntity={entityConfig.updateEntity}
              deleteEntity={entityConfig.deleteEntity}
              getEntityDetails={entityConfig.getEntityDetails}
              listEntities={entityConfig.listEntities}
              listEntitiesByIds={entityConfig.listEntitiesByIds}
              onCreatedEntityChange={onEntityReady || (() => { })}
              isParent={true}
              fieldsToHide={fieldsToHide}
              summaryProps={entityConfig.summaryProps}
              enableCreate={enableCreate}
              enableSearchBar={false}
              enableActionButtons={true}
            />
            {selectedEntityId && renderDetailExtra ? renderDetailExtra(selectedEntityId) : null}
          </div>
        </div>
      )}
    </PageWrapper>
  );
}